import React, { Suspense, useState } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
const W1 = React.lazy(() => import('@/components/landing/product/main/datasets/stocks'));
const W11 = React.lazy(() => import('@/components/landing/product/main/datasets/pooled-vehicle-comparison-card'));

const W2 = React.lazy(() => import('@/components/landing/product/main/datasets/macro'));
const W22 = React.lazy(() => import('@/components/landing/product/main/datasets/market-breadth-vehicle-card'));

const W3 = React.lazy(() => import('@/components/landing/product/main/datasets/index-leadership-card'));
const W33 = React.lazy(() => import('@/components/landing/product/main/datasets/crypto-liquidity-map-card'));

import { breakpoints } from '@/layouts/breakpoints';

const items = [
  {
    title: 'Every market, one unified feed.',
    image: '/product_images/3.webp',
    description:
      'Stocks, ETFs, and pooled vehicles sit side by side in a single dataset, so you can compare exposure, performance, and fundamentals without reconciling formats across a dozen separate feeds.',
  },
  {
    title: 'Macro context, not just tickers.',
    image: '/product_images/14.webp',
    description:
      'Economic indicators, calendar events, and market breadth data sit alongside your price and fundamentals data, so every signal comes with the context to explain why it matters.',
  },
  {
    title: 'Cross-asset coverage, always current.',
    image: '/product_images/1.webp',
    description:
      'Index leadership shifts and crypto liquidity across equities, indices, and digital assets, updated daily and hourly so your long-horizon research always reflects the latest market state.',
  },
];

const StyledComp = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-template-rows: auto auto;
  column-gap: 64px;
  row-gap: 0;
  width: calc(100% - 80px);
  padding: 48px 40px;
  padding-bottom: 120px;
  max-width: 1400px;
  margin: 0 auto;


  @media (max-width: ${breakpoints.small}) {
    padding: 48px 24px;
    width: calc(100% - 48px);
    padding-bottom: 20px;
  }

  .heading {
    grid-column: 1;
    grid-row: 2;
    font-size: 32px;
    line-height: 1.28;
    font-weight: 500;
    color: var(--black);
    margin: 0;
    max-width: 420px;
  }

  .description {
    grid-column: 2;
    grid-row: 1 / span 2;
    align-self: end;
    font-size: 15px;
    line-height: 1.65;
    color: var(--grey);
    margin: 0;
    max-width: 540px;
    text-align: right;
    justify-self: end;

    @media (max-width: ${breakpoints.small}) {
      text-align: left;
    }
  }

  .graphic {
    grid-column: 1;
    grid-row: 3;
    margin-top: 40px;
    position: relative;
    border-radius: 20px;
    overflow: hidden;
    aspect-ratio: 1 / 1.02;
    background: rgba(var(--blackr), 1);
  }

  .accordion {
    grid-column: 2;
    grid-row: 3;
    align-self: start;
    border-top: 1px solid rgba(var(--greyr), 0.25);
    align-self: end;
  }

  @media (max-width: 860px) {
    grid-template-columns: 1fr;

    .heading,
    .description,
    .graphic,
    .accordion {
      grid-column: 1;
    }

    .heading { grid-row: 2; }
    .description {
      grid-row: 3;
      align-self: start;
      margin-top: 16px;
      max-width: none;
    }
    .graphic { grid-row: 4; }
    .accordion { grid-row: 5; }
  }
`;

const GraphicLayer = styled(motion.div)`
  position: absolute;
  background-color: white;
  background-size: cover;
  inset: 0;
  padding: 20px;
`;

const AccordionItemEl = styled.div`
  border-bottom: 1px solid rgba(var(--greyr), 0.25);
`;

const AccordionHeader = styled.button`
  all: unset;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  width: 100%;
  padding: 20px 0;
  cursor: pointer;
  font-size: 15px;
  color: var(--black);
  transition: color 0.2s ease;

  &:hover {
    color: rgba(var(--blackr), 0.65);
  }

  &:focus-visible {
    outline: 2px solid var(--purple);
    outline-offset: 4px;
  }
`;

const Chevron = styled(motion.span)`
  display: inline-flex;
  flex-shrink: 0;
  color: var(--grey);
`;

const AccordionBody = styled(motion.div)`
  overflow: hidden;
`;

const AccordionBodyInner = styled.div`
  padding: 0 0 20px;
  font-size: 14px;
  line-height: 1.65;
  color: var(--grey);
  max-width: 460px;
`;

const Comp = () => {
  const [activeIndex, setActiveIndex] = useState<number>(0);

  const handleToggle = (index: number) => {
    setActiveIndex((prev) => (prev === index ? -1 : index));
  };


  const comps = [
    (
      <>
        <W1/>
        <div style={{height: '20px'}}/>
        <W11/>
      </>
    ),
    (<>
      <W2/>
      <div style={{height: '20px'}}/>
      <W22/>
    </>),
    (<div style={{top: '-500px'}}>
      <W3/>
      <div style={{height: '20px'}}/>
      <W33/>
    </div>)
  ]

  return (
    <StyledComp>

      <h2 className="heading serif">A Clear Path From Raw Data to Real Conviction</h2>

      <p className="description">
        Axion unifies market data, fundamentals, alternative data, and quant tools into one
        financial intelligence API built for hedge funds, quant teams, and fintech builders, delivering structured,
        model-ready data you can act on immediately.
      </p>

      <div className="graphic">
        <AnimatePresence mode="wait" >
          <GraphicLayer
            key={activeIndex}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.45, ease: 'easeInOut' }}
            style={{
              backgroundImage: `url('${items[activeIndex].image}')`,
            }}
          >
          <Suspense fallback={null}>{comps[activeIndex]}</Suspense>
          </GraphicLayer>
        </AnimatePresence>
      </div>

      <div className="accordion">
        {items.map((item, index) => {
          const isOpen = activeIndex === index;

          return (
            <AccordionItemEl key={item.title}>
              <AccordionHeader
                onClick={() => handleToggle(index)}
                aria-expanded={isOpen}
              >
                {item.title}
                <Chevron
                  animate={{ rotate: isOpen ? 180 : 0 }}
                  transition={{ duration: 0.3, ease: 'easeInOut' }}
                >
                  <svg width="12" height="8" viewBox="0 0 12 8" fill="none">
                    <path
                      d="M1 1.5L6 6.5L11 1.5"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </Chevron>
              </AccordionHeader>

              <AnimatePresence initial={false}>
                {isOpen && (
                  <AccordionBody
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.35, ease: 'easeInOut' }}
                  >
                    <AccordionBodyInner>{item.description}</AccordionBodyInner>
                  </AccordionBody>
                )}
              </AnimatePresence>
            </AccordionItemEl>
          );
        })}
      </div>
    </StyledComp>
  );
};

export default Comp;
